import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage } from "src/components";
import DropdownSelector from "src/components/DropdownSelector";
import { useMultiSelectOnChange } from "src/hooks";

import { enums, formatters } from "@fraction/shared";

const { ETHNICITY_MAP } = formatters.enums;

export interface EthnicityProps {
  onChange: (value: enums.Ethnicity[]) => void;
  value?: enums.Ethnicity[];
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
}

/**
 * This doesn't need to be an array, but it keeps it consistent with EthnicityRegC, which we might move
 * back to at some point.
 */
const Ethnicity = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  children,
  ...props
}: EthnicityProps) => {
  const validate = useCallback(() => !!value, [value]);
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const handleChange = useMultiSelectOnChange<enums.Ethnicity>(onChange);

  return (
    <FormFlowPage
      {...props}
      title={title || "What is your ethnicity?"}
      description={
        "Federal regulations require us to ask. " +
        "We do not discriminate on the basis of this information or whether you choose to provide it."
      }
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <DropdownSelector
        label="Ethnicity"
        focusRef={focusRef}
        value={value?.[0]}
        onChange={handleChange}
        options={[
          enums.Ethnicity.NOT_HISPANIC_OR_LATINO,
          enums.Ethnicity.HISPANIC_OR_LATINO,
          enums.Ethnicity.NOT_DISCLOSED,
        ].map((type) => ({
          value: type,
          label: ETHNICITY_MAP[type],
        }))}
      />
      {children}
    </FormFlowPage>
  );
};

export default memo(Ethnicity);
